package fr.frux.feature.map.aim

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.layout.layout
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.icons.glyphFor
import fr.frux.core.maps.MapCameraState
import fr.frux.core.maps.metersToDp
import fr.frux.core.maps.offsetOfLocation
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_aim_current_position_description
import fr.frux.feature.map.resources.map_aim_distance
import fr.frux.feature.map.speciesFamilyColor
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

private val CurrentPositionSize = 34.dp
private val CurrentPositionGlyph = 18.dp
private const val CURRENT_POSITION_OPACITY = 0.85f
private val DistanceChipGap = 12.dp
private val DistanceChipRise = 34.dp
private const val CIRCLE_FILL_ALPHA = 0.08f

/**
 * Cercle de borne, trait vers la pointe et position actuelle de l'arbre. Le centre se relit dans
 * la phase de dessin et de mise en page : la camera bouge a chaque frame, la composition non.
 */
@Composable
internal fun BoxScope.AimCircleLayer(
    camera: MapCameraState,
    circle: AimCircle,
    pinOffset: DpOffset,
    outside: Boolean,
) {
    val primary = MaterialTheme.colorScheme.primary
    val lineColor = if (outside) MaterialTheme.colorScheme.error else primary
    Canvas(modifier = Modifier.fillMaxSize()) {
        val position = camera.position
        val offset = offsetOfLocation(position, circle.center)
        val center = Offset(size.width / 2 + offset.x.toPx(), size.height / 2 + offset.y.toPx())
        val radius = metersToDp(circle.radiusMeters, circle.center.latitude, position.zoom).toPx()
        drawCircle(primary.copy(alpha = CIRCLE_FILL_ALPHA), radius, center)
        drawCircle(primary, radius, center, style = dashedStroke(6.dp.toPx()))
        val tip = Offset(size.width / 2 + pinOffset.x.toPx(), size.height / 2 + pinOffset.y.toPx())
        drawLine(lineColor, center, tip, strokeWidth = 2.dp.toPx(), pathEffect = dashEffect(4.dp.toPx()))
    }
    CurrentPositionMarker(camera, circle)
}

private fun DrawScope.dashedStroke(dash: Float) = Stroke(width = 2.dp.toPx(), pathEffect = dashEffect(dash))

private fun dashEffect(dash: Float) = PathEffect.dashPathEffect(floatArrayOf(dash, dash))

@Composable
private fun BoxScope.CurrentPositionMarker(
    camera: MapCameraState,
    circle: AimCircle,
) {
    val description = stringResource(Res.string.map_aim_current_position_description)
    val primary = MaterialTheme.colorScheme.primary
    val tint =
        circle.scientificName?.let { speciesFamilyColor(it, null) } ?: primary
    val glyph = circle.scientificName?.let { glyphFor(it, null) } ?: Icons.Outlined.Park
    Box(
        modifier =
            Modifier
                .align(Alignment.Center)
                .offset {
                    val offset = offsetOfLocation(camera.position, circle.center)
                    IntOffset(offset.x.roundToPx(), offset.y.roundToPx())
                }.alpha(CURRENT_POSITION_OPACITY)
                .size(CurrentPositionSize)
                .semantics { contentDescription = description },
        contentAlignment = Alignment.Center,
    ) {
        val surface = MaterialTheme.colorScheme.surface
        Canvas(modifier = Modifier.fillMaxSize()) {
            drawCircle(surface)
            val stroke = 2.dp.toPx()
            drawCircle(
                primary,
                radius = (size.minDimension - stroke) / 2,
                style = Stroke(width = stroke, pathEffect = dashEffect(4.dp.toPx())),
            )
        }
        Icon(glyph, contentDescription = null, tint = tint, modifier = Modifier.size(CurrentPositionGlyph))
    }
}

/** Pastille de distance a droite de la pointe, « 14 m » : `errorContainer` au-dela du cercle. */
@Composable
internal fun BoxScope.AimDistanceChip(
    aim: MapAimState,
    pinOffset: DpOffset,
    outside: Boolean,
) {
    val meters by remember(aim) { derivedStateOf { aim.targetDistanceMeters?.roundToInt() } }
    val value = meters ?: return
    Surface(
        shape = CircleShape,
        color = if (outside) MaterialTheme.colorScheme.errorContainer else MaterialTheme.colorScheme.surface,
        contentColor =
            if (outside) MaterialTheme.colorScheme.onErrorContainer else MaterialTheme.colorScheme.onSurface,
        shadowElevation = 2.dp,
        modifier =
            Modifier.layout { measurable, constraints ->
                val placeable = measurable.measure(constraints.copy(minWidth = 0, minHeight = 0))
                layout(placeable.width, placeable.height) {
                    val x = constraints.maxWidth / 2 + (pinOffset.x + DistanceChipGap).roundToPx()
                    val y = constraints.maxHeight / 2 + (pinOffset.y - DistanceChipRise).roundToPx()
                    placeable.place(x, y - placeable.height / 2)
                }
            },
    ) {
        Text(
            text = stringResource(Res.string.map_aim_distance, value),
            style = MaterialTheme.typography.labelMedium,
            modifier = Modifier.padding(horizontal = 10.dp).padding(vertical = 5.dp),
        )
    }
}
